import { useEffect, useState } from "react";

import type { DocumentSource, PageMetrics, ViewportRenderState } from "@/features/document-viewport/types";

interface ImageViewportProps {
  source: DocumentSource;
  baseWidth: number;
  zoom: number;
  onPageCountChange: (pageCount: number) => void;
  onPageMetricsChange: (metrics: PageMetrics) => void;
  onPageStatusChange: (
    pageNumber: number,
    status: ViewportRenderState,
    errorMessage?: string
  ) => void;
  onRenderCommit: () => void;
}

export function ImageViewport({
  source,
  baseWidth,
  zoom,
  onPageCountChange,
  onPageMetricsChange,
  onPageStatusChange,
  onRenderCommit,
}: ImageViewportProps) {
  const [displayUrl, setDisplayUrl] = useState(source.fileUrl);

  useEffect(() => {
    setDisplayUrl(source.fileUrl);
  }, [source.revisionId]);

  useEffect(() => {
    onPageCountChange(1);
  }, [onPageCountChange]);

  return (
    <img
      src={displayUrl}
      alt={source.filename}
      draggable={false}
      className="block rounded-md border bg-background shadow-sm"
      style={{
        width: `${baseWidth}px`,
        transform: `scale(${zoom})`,
        transformOrigin: "top left",
      }}
      onLoad={(event) => {
        const image = event.currentTarget;
        if (!image.naturalWidth || !image.naturalHeight) {
          onPageStatusChange(1, "error", "Image dimensions unavailable");
          return;
        }

        onPageMetricsChange({
          pageNumber: 1,
          width: image.naturalWidth,
          height: image.naturalHeight,
          aspectRatio: image.naturalHeight / image.naturalWidth,
          rotation: 0,
        });
        onPageStatusChange(1, "ready");
        onRenderCommit();
      }}
      onError={() => {
        onPageStatusChange(1, "error", "Failed to load image");
      }}
    />
  );
}
